import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Globe2, Loader2, MapPin } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { RheaPrototypeHeader } from "@/components/RheaPrototypeHeader";
import {
  exploreDestinations,
  type DestinationCoverageCountry,
  type DestinationExploreResult,
} from "@/lib/api";

export const Route = createFileRoute("/explore")({
  component: DestinationExplorePage,
  validateSearch: (search: Record<string, unknown>) => {
    const safeString = (value: unknown, maxLength: number): string | undefined => {
      if (typeof value !== "string") return undefined;
      const normalized = value.trim();
      return normalized.length > 0 && normalized.length <= maxLength
        ? normalized
        : undefined;
    };
    const safeInt = (value: unknown, min: number, max: number): number | undefined => {
      if (typeof value !== "string" && typeof value !== "number") return undefined;
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed >= min && parsed <= max
        ? parsed
        : undefined;
    };
    return {
      scope: safeString(search.scope, 120),
      intent: safeString(search.intent, 300),
      departure_date: safeString(search.departure_date, 10),
      return_date: safeString(search.return_date, 10),
      travel_month: safeString(search.travel_month, 10),
      flexible_days: safeInt(search.flexible_days, 1, 90),
      flexibility_window: safeInt(search.flexibility_window, 0, 30),
      adults: safeInt(search.adults, 1, 10),
      children_ages: safeString(search.children_ages, 80),
    };
  },
  head: () => ({
    meta: [
      { title: "Explorar destinos — Sift Trip" },
      {
        name: "description",
        content: "Compara la cobertura observada de experiencias por país y ciudad.",
      },
    ],
  }),
});

function formatCountryCoverage(country: DestinationCoverageCountry): string {
  if (country.activity_count === null) return "Inventario por verificar";
  const suffix = country.count_is_complete ? "" : "+";
  return `${country.activity_count}${suffix} experiencias observadas`;
}

function DestinationExplorePage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [result, setResult] = useState<DestinationExploreResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!search.scope) {
      setLoading(false);
      setError(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    exploreDestinations(search.scope)
      .then((response) => {
        if (!cancelled) setResult(response);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [search.scope]);

  const summaryCopy = useMemo(() => {
    if (!result || result.summary.activity_count === null) {
      return "La cobertura se confirmará ciudad por ciudad.";
    }
    const suffix = result.summary.count_is_complete ? "" : "+";
    return `${result.summary.activity_count}${suffix} experiencias observadas en ${result.summary.observed_city_count} ciudades.`;
  }, [result]);

  const openCity = (iata: string, name: string) => {
    void navigate({
      to: "/experiences",
      search: {
        q: search.intent,
        destination_iata: iata,
        destination_name: name,
        departure_date: search.departure_date,
        return_date: search.return_date,
        travel_month: search.travel_month,
        flexible_days: search.flexible_days,
        flexibility_window: search.flexibility_window,
        adults: search.adults,
        children_ages: search.children_ages,
      },
    });
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-[#101522]">
      <RheaPrototypeHeader mode="workspace" />
      <main className="mx-auto max-w-6xl px-5 pb-16 pt-24 sm:px-8">
        <button
          type="button"
          onClick={() => void navigate({ to: "/" })}
          className="mb-7 inline-flex items-center gap-2 text-sm font-semibold text-[#4c566d] transition-colors hover:text-[#101522] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1f65d6]"
        >
          <ArrowLeft size={16} aria-hidden />
          Cambiar destino
        </button>

        {loading && (
          <div className="flex min-h-[48vh] items-center justify-center gap-3 text-sm text-[#5f687c]">
            <Loader2 size={19} className="animate-spin" aria-hidden />
            Consultando cobertura observada
          </div>
        )}

        {!loading && error && (
          <div className="mx-auto max-w-xl py-24 text-center">
            <Globe2 size={30} className="mx-auto mb-4 text-[#69748b]" aria-hidden />
            <h1 className="text-2xl font-semibold">No pudimos abrir este destino</h1>
            <p className="mt-2 text-sm text-[#687186]">
              Selecciona nuevamente un país, continente o región desde el buscador.
            </p>
          </div>
        )}

        {!loading && !error && result && (
          <>
            <header className="border-b border-[#dfe3ea] pb-8">
              <p className="mb-2 text-xs font-bold uppercase text-[#687186]">
                Explorar por geografía
              </p>
              <h1 className="text-3xl font-semibold sm:text-4xl">
                {result.scope.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-[#5f687c]">
                {summaryCopy}
              </p>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#3f4a61]">
                <span>
                  {result.summary.country_count}{" "}
                  {result.summary.country_count === 1 ? "país" : "países"}
                </span>
                <span>
                  {result.summary.city_count}{" "}
                  {result.summary.city_count === 1 ? "ciudad disponible" : "ciudades disponibles"}
                </span>
              </div>
            </header>

            <div className="divide-y divide-[#dfe3ea]">
              {result.countries.map((country) => (
                <section key={country.iso2 ?? country.name} className="py-9">
                  <div className="mb-5 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
                    <div>
                      <h2 className="text-xl font-semibold">{country.name}</h2>
                      <p className="mt-1 text-xs text-[#687186]">
                        {country.city_count} ciudades · {formatCountryCoverage(country)}
                      </p>
                    </div>
                    {!country.count_is_complete && (
                      <span className="text-xs text-[#788196]">
                        Datos para {country.observed_city_count} de {country.city_count} ciudades
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {country.cities.map((city) => (
                      <button
                        key={city.iata}
                        type="button"
                        onClick={() => openCity(city.iata, city.name)}
                        className="group flex min-h-[116px] items-center gap-4 border border-[#d9dee7] bg-white px-4 py-4 text-left transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-[#aeb8ca] hover:shadow-[0_8px_24px_rgba(20,31,52,0.08)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f65d6]"
                        style={{ borderRadius: "8px" }}
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#edf2fa] text-[#1f65d6]">
                          <MapPin size={18} aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-2">
                            <span className="truncate text-base font-semibold">{city.name}</span>
                            <span className="text-[10px] font-bold text-[#7a8498]">{city.iata}</span>
                          </span>
                          <span className="mt-1 block text-xs text-[#687186]">
                            {city.activity_count === null
                              ? "Inventario por verificar"
                              : `${city.activity_count} experiencias observadas`}
                          </span>
                        </span>
                        <ArrowRight
                          size={16}
                          className="shrink-0 text-[#8791a4] transition-transform group-hover:translate-x-0.5"
                          aria-hidden
                        />
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
